/**
 * PostComposer Camera Composable
 *
 * Extracted from PostComposer.vue — handles live camera preview,
 * photo capture, video recording, and shutter controls.
 *
 * Zero behavior change: this is a structural extraction only.
 */

import { ref, onUnmounted } from "vue";

/**
 * @param {Object} deps - External dependencies from PostComposer
 * @param {import('vue').Ref} deps.imageInput - Image file input ref
 * @param {import('vue').Ref} deps.imageLibraryInput - Image library file input ref
 * @param {import('vue').Ref} deps.videoLibraryInput - Video library file input ref
 * @param {import('vue').Ref} deps.selectedType - Current media type ref
 * @param {import('vue').Ref} deps.viewMode - Current view mode ref
 * @param {Function} deps.addMediaItems - Adds files to the composer
 * @param {Function} deps.clearOtherTypes - Clears media types other than the given one
 * @param {Function} deps.openUploadPicker - Opens the unified file picker
 * @param {Function} deps.log - Debug logger
 */
export function usePostComposerCamera(deps) {
  const {
    imageInput,
    imageLibraryInput,
    videoLibraryInput,
    selectedType,
    viewMode,
    addMediaItems,
    clearOtherTypes,
    openUploadPicker,
    log,
  } = deps;

  // --- Refs ---
  const captureMode = ref(false);
  const cameraError = ref("");
  const cameraPreviewRef = ref(null);
  const cameraTileRef = ref(null);
  const isRecording = ref(false);
  const recordingProgressDeg = ref(0);
  const currentFacingMode = ref("environment");

  // --- Internal state (non-reactive) ---
  let cameraStream = null;
  let mediaRecorder = null;
  const recordedChunks = [];
  const recordingTimeout = ref(null);
  const maxRecordingMs = 60000; // 60s cap
  let shutterTimer = null;

  // --- Helpers ---

  const isMobile = () => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || navigator.vendor || "";
    return /android|iphone|ipad|ipod|mobile/i.test(ua);
  };

  const canUseCamera = () => {
    try {
      const hasAPI = !!(
        typeof navigator !== "undefined" &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === "function"
      );
      const secure =
        typeof window !== "undefined"
          ? window.isSecureContext !== false
          : true;
      return hasAPI && secure;
    } catch {
      return false;
    }
  };

  const canShowCameraIcon = () => {
    try {
      return canUseCamera() || isMobile();
    } catch {
      return false;
    }
  };

  // --- Core camera operations ---

  const stopCamera = () => {
    try {
      mediaRecorder?.stop?.();
    } catch {}
    try {
      cameraStream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    cameraStream = null;
  };

  const openLiveCamera = async () => {
    log("openLiveCamera:start");
    cameraError.value = "";
    if (!canUseCamera()) {
      cameraError.value = "Camera not supported in this context";
      log("openLiveCamera:unsupported -> fallback", { isMobile: isMobile() });
      if (isMobile()) imageInput.value?.click();
      else imageLibraryInput.value?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: currentFacingMode.value } },
        audio: false,
      });
      cameraStream = stream;
      const bindAndPlay = async (el) => {
        if (!el) return;
        try {
          el.srcObject = stream;
          const tryPlay = async () => {
            try {
              await el.play?.();
            } catch {}
          };
          if (el.readyState >= 1) await tryPlay();
          else el.onloadedmetadata = () => tryPlay();
        } catch {}
      };
      await Promise.all([
        bindAndPlay(cameraPreviewRef.value),
        bindAndPlay(cameraTileRef.value),
      ]);
      try {
        const tracks = stream?.getVideoTracks?.() || [];
        const settings = tracks[0]?.getSettings?.() || {};
        log("openLiveCamera:success", { tracks: tracks.length, settings });
      } catch {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PostComposer] Camera preview failed:", e);
      cameraError.value = e?.message || String(e);
      log("openLiveCamera:error", e?.name || e?.message || e);
      if (isMobile()) imageInput.value?.click();
      else imageLibraryInput.value?.click();
    }
  };

  const grabPhotoFrame = async () => {
    try {
      const video = cameraPreviewRef.value;
      if (!video) return null;
      const canvas = document.createElement("canvas");
      const w = video.videoWidth || 1080;
      const h = video.videoHeight || 1920;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);
      const blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", 1.0),
      );
      if (!blob) return null;
      const file = new File([blob], `photo_${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      return file;
    } catch (e) {
      log("grabPhotoFrame:error", e?.message || e);
      return null;
    }
  };

  const stopRecording = () => {
    try {
      mediaRecorder?.stop?.();
    } catch {}
  };

  const startRecording = async () => {
    if (!cameraStream) return;
    recordingProgressDeg.value = 0;
    recordedChunks.length = 0;
    try {
      mediaRecorder = new MediaRecorder(cameraStream, {
        mimeType: "video/webm;codecs=vp9",
      });
    } catch {
      try {
        mediaRecorder = new MediaRecorder(cameraStream, {
          mimeType: "video/webm",
        });
      } catch (e2) {
        log("MediaRecorder unsupported", e2?.message || e2);
        return;
      }
    }
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordedChunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      isRecording.value = false;
      clearTimeout(recordingTimeout.value);
      try {
        const blob = new Blob(recordedChunks, {
          type: recordedChunks[0]?.type || "video/webm",
        });
        const file = new File([blob], `video_${Date.now()}.webm`, {
          type: blob.type,
        });
        addMediaItems([file]);
        stopCamera();
      } catch (e) {
        log("onstop concat error", e?.message || e);
      }
    };
    isRecording.value = true;
    mediaRecorder.start(100);
    const start = performance.now();
    const tick = () => {
      if (!isRecording.value) return;
      const elapsed = performance.now() - start;
      const pct = Math.min(elapsed / maxRecordingMs, 1);
      recordingProgressDeg.value = Math.floor(pct * 360);
      if (pct >= 1) return stopRecording();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    recordingTimeout.value = setTimeout(
      () => stopRecording(),
      maxRecordingMs,
    );
  };

  // --- Shutter controls ---

  const switchCamera = async () => {
    if (isRecording.value) return; // don't switch while recording
    currentFacingMode.value =
      currentFacingMode.value === "environment" ? "user" : "environment";
    log("switchCamera", { facingMode: currentFacingMode.value });
    stopCamera();
    await openLiveCamera();
  };

  const onShutterPressStart = () => {
    log("onShutterPressStart");
    shutterTimer = setTimeout(() => {
      log("shutter long-press -> startRecording");
      startRecording();
    }, 350);
  };

  const onShutterPressEnd = async () => {
    log("onShutterPressEnd");
    if (shutterTimer) {
      clearTimeout(shutterTimer);
      shutterTimer = null;
      if (!isRecording.value) {
        const photo = await grabPhotoFrame();
        if (photo) {
          addMediaItems([photo]);
          log("photo captured", { size: photo.size, appended: true });
        }
      } else {
        stopRecording();
      }
    } else if (isRecording.value) {
      stopRecording();
    }
  };

  const onShutterPressCancel = () => {
    log("onShutterPressCancel");
    if (shutterTimer) {
      clearTimeout(shutterTimer);
      shutterTimer = null;
    }
  };

  // --- Navigation helpers ---

  const openDevicePicker = () => {
    log("openDevicePicker", { selectedType: selectedType.value });
    if (selectedType.value === "video") videoLibraryInput.value?.click();
    else imageLibraryInput.value?.click();
  };

  const onCameraTileClick = () => {
    log("onCameraTileClick");
    selectedType.value = "photos";
    clearOtherTypes("photos");
    captureMode.value = true;
    openLiveCamera();
  };

  const onCameraFooterClick = () => {
    log("onCameraFooterClick");
    viewMode.value = "edit";
    selectedType.value = "photos";
    clearOtherTypes("photos");
    const supported = canUseCamera();
    if (supported) {
      captureMode.value = true;
      openLiveCamera();
    } else {
      if (isMobile()) {
        imageInput.value?.click?.();
      } else {
        openUploadPicker();
      }
    }
  };

  // --- Cleanup ---
  onUnmounted(() => {
    stopCamera();
  });

  return {
    // Refs
    captureMode,
    cameraError,
    cameraPreviewRef,
    cameraTileRef,
    isRecording,
    recordingProgressDeg,
    currentFacingMode,
    // Functions
    isMobile,
    canUseCamera,
    canShowCameraIcon,
    stopCamera,
    openLiveCamera,
    grabPhotoFrame,
    startRecording,
    stopRecording,
    switchCamera,
    onShutterPressStart,
    onShutterPressEnd,
    onShutterPressCancel,
    openDevicePicker,
    onCameraTileClick,
    onCameraFooterClick,
  };
}
